/**
 * Progression data: class stat profiles, gem bonuses, legendaries, trinkets.
 * SAMPLE content (enough to exercise every code path); the content builder fills it out.
 */
import { ABILITY, CLASS } from '@keystone/content';
import type { ClassId, Role } from '@keystone/sim';
import { LEGENDARIES } from './items.legendaries';
import { TRINKETS } from './items.trinkets';
import type { ClassStatProfile, LegendaryDef, TrinketEffectDef } from './types';

export const CLASS_STAT_PROFILES: Readonly<Record<ClassId, ClassStatProfile>> = {
  [CLASS.warden]: {
    classId: CLASS.warden,
    priority: ['expertise', 'haste', 'spirit', 'crit'],
    // Warden expertise: more self-healing from Bulwark Strike, less damage taken.
    expertise: [
      { stat: 'healingDonePct', value: 1, abilityIds: [ABILITY.warden.bulwarkStrike] },
      { stat: 'damageTakenPct', value: -0.25 },
    ],
  },
  [CLASS.lightbinder]: {
    classId: CLASS.lightbinder,
    priority: ['crit', 'haste', 'expertise', 'spirit'],
    expertise: [{ stat: 'healingDonePct', value: 1 }],
  },
  [CLASS.blademaster]: {
    classId: CLASS.blademaster,
    priority: ['haste', 'crit', 'expertise', 'spirit'],
    expertise: [{ stat: 'damageDonePct', value: 1, abilityIds: [ABILITY.blademaster.mortalStrike, ABILITY.blademaster.execute] }],
  },
  [CLASS.stormcaller]: {
    classId: CLASS.stormcaller,
    priority: ['haste', 'expertise', 'crit', 'spirit'],
    expertise: [{ stat: 'damageDonePct', value: 1, school: 'nature' }],
  },
};

/**
 * +classes: the five base classes. Expertise is generic for the plain kits (specs at level 10 get
 * their own): tanks take less damage, the healer heals more, damage dealers hit harder.
 */
Object.assign(CLASS_STAT_PROFILES as Record<ClassId, ClassStatProfile>, {
  warrior: { classId: 'warrior', priority: ['expertise', 'haste', 'spirit', 'crit'], expertise: [{ stat: 'damageTakenPct', value: -0.25 }] },
  acolyte: { classId: 'acolyte', priority: ['crit', 'haste', 'expertise', 'spirit'], expertise: [{ stat: 'healingDonePct', value: 1 }] },
  rogue: { classId: 'rogue', priority: ['crit', 'haste', 'expertise', 'spirit'], expertise: [{ stat: 'damageDonePct', value: 1, school: 'physical' }] },
  mage: { classId: 'mage', priority: ['haste', 'crit', 'expertise', 'spirit'], expertise: [{ stat: 'damageDonePct', value: 1 }] },
  ranger: { classId: 'ranger', priority: ['crit', 'haste', 'expertise', 'spirit'], expertise: [{ stat: 'damageDonePct', value: 1, school: 'physical' }] },
} satisfies Record<string, ClassStatProfile>);

export const ROLE_DEFAULT_CLASS: Readonly<Record<Role, ClassId>> = {
  tank: CLASS.warden,
  healer: CLASS.lightbinder,
  dps: CLASS.blademaster,
};

/** Gem Power per gem rank (index = rank). */
export const GEM_POWER_BY_RANK: readonly number[] = [0, 30, 45, 60, 80, 100];
export const MAX_GEM_RANK = GEM_POWER_BY_RANK.length - 1;
export const GEM_THRESHOLDS: readonly number[] = [120, 240, 360, 480];

// Gem bonuses, legendaries and trinkets live next to the systems that use them (gems.bonuses.ts,
// items.legendaries.ts, items.trinkets.ts) and are re-exported here so compile/UI keep one import.
export { GEM_BONUSES } from './gems.bonuses';
export { LEGENDARIES } from './items.legendaries';
export { TRINKETS } from './items.trinkets';

export const legendary = (id: string): LegendaryDef | undefined => LEGENDARIES.find((l) => l.id === id);
export const trinketEffect = (id: string): TrinketEffectDef | undefined => TRINKETS.find((t) => t.id === id);

